<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from 'vue-router'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias

resultsStore.fetchResults(eventAlias)

const now = ref(Date.now())
const refreshIntervalMs = 30000
const clockIntervalMs = 1000
const recentlyChanged = ref({})

let refreshResultsTimer = setInterval(() => {
  resultsStore.fetchResults(route.params.eventAlias)
}, refreshIntervalMs)

let clockTimer = null
let previousOrder = []

const teams = computed(() => {
  const grouped = new Map()

  for (const runner of resultsStore.results) {
    if (runner.category_id === 10) continue

    const teamName = (runner.bio || '').trim()
    if (!teamName) continue

    if (!grouped.has(teamName)) {
      grouped.set(teamName, [])
    }

    grouped.get(teamName).push(buildRunnerEntry(runner))
  }

  const computedTeams = []

  grouped.forEach((members, teamName) => {
    if (members.length < 4) return

    const womenCount = members.filter(member => member.category_id === 30).length
    if (womenCount < 1) return

    const eligible = members
      .filter(member => !member.isDnf && !member.isDns && member.timeMs != null)
      .sort((a, b) => a.timeMs - b.timeMs)

    let selected = eligible.slice(0, 4)

    if (selected.length === 4 && !selected.some(member => member.category_id === 30)) {
      const bestWoman = eligible.find(member => member.category_id === 30)
      if (bestWoman) {
        selected = [...selected.slice(0, 3), bestWoman].sort((a, b) => a.timeMs - b.timeMs)
      }
    }

    const isClassified = selected.length === 4 && selected.some(member => member.category_id === 30)
    const selectedIds = new Set(selected.map(member => member.id))

    const teamMembers = members
      .slice()
      .sort((a, b) => {
        if (a.timeMs != null && b.timeMs != null) return a.timeMs - b.timeMs
        if (a.timeMs != null) return -1
        if (b.timeMs != null) return 1
        return a.number - b.number
      })
      .map(member => ({
        ...member,
        counted: selectedIds.has(member.id)
      }))

    const totalTimeMs = isClassified
      ? selected.reduce((sum, member) => sum + member.timeMs, 0)
      : null

    computedTeams.push({
      teamName,
      members: teamMembers,
      selected,
      totalTimeMs,
      isClassified
    })
  })

  return computedTeams.sort((a, b) => {
    if (a.isClassified && !b.isClassified) return -1
    if (!a.isClassified && b.isClassified) return 1
    if (!a.isClassified && !b.isClassified) return a.teamName.localeCompare(b.teamName, 'pl')
    if (a.totalTimeMs !== b.totalTimeMs) return a.totalTimeMs - b.totalTimeMs
    return a.teamName.localeCompare(b.teamName, 'pl')
  })
})

watch(
  () => teams.value.map((team, index) => getTeamHighlightKey(team, index)),
  (newOrder) => {
    if (previousOrder.length === 0) {
      previousOrder = [...newOrder]
      return
    }

    const nowTime = Date.now()
    newOrder.forEach((entry, index) => {
      if (previousOrder[index] !== entry) {
        recentlyChanged.value[entry] = nowTime
      }
    })

    previousOrder = [...newOrder]
  },
  { immediate: true }
)

onMounted(() => {
  clockTimer = setInterval(() => {
    now.value = Date.now()

    const cutoff = Date.now() - 30 * 1000
    for (const key in recentlyChanged.value) {
      if (recentlyChanged.value[key] < cutoff) {
        delete recentlyChanged.value[key]
      }
    }
  }, clockIntervalMs)
})

onUnmounted(() => {
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
  if (clockTimer) clearInterval(clockTimer)
})

function buildRunnerEntry(runner) {
  const isDns = Boolean(runner.abs)
  const isDnf = Boolean(runner.dnf)

  const timeMs = getRunnerTimeMs(runner)

  return {
    id: runner.id,
    number: runner.number,
    name: runner.name,
    category_id: runner.category_id,
    isDnf,
    isDns,
    timeMs
  }
}

function getRunnerTimeMs(runner) {
  if (runner.abs || runner.dnf) return null

  const startMs = parseTime(runner.start_time)
  if (startMs == null) return null

  const finishMs = parseTime(runner.finish_time) ?? parseTime(runner.finish_time_gps)
  const endMs = finishMs ?? now.value

  const elapsed = endMs - startMs
  if (!Number.isFinite(elapsed) || elapsed < 0) return null

  return elapsed
}

function parseTime(value) {
  if (!value) return null
  const date = new Date(value)
  const timestamp = date.getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

function formatRunnerTime(member) {
  if (member.isDns) return 'DNS'
  if (member.isDnf) return 'DNF'
  if (member.timeMs == null) return '--:--:--'
  return formatDuration(member.timeMs)
}

function formatTeamTotal(team) {
  if (!team.isClassified || team.totalTimeMs == null) {
    return 'NKL'
  }
  return formatDuration(team.totalTimeMs)
}

function getTeamHighlightKey(team, index) {
  return `${team.teamName}:${team.isClassified ? index + 1 : 'NKL'}`
}

function formatDuration(milliseconds) {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    return '--:--:--'
  }

  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}
</script>

<template>
  <div>
    <h1>Wyniki zespołów</h1>

    <div class="status-bar mb-3">
      <span class="badge bg-info">Aktualizacja co 30 sekund</span>
      <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
    </div>

    <div class="table-scroll">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zespół</th>
            <th>Skład i czasy</th>
            <th>Suma 4 czasów</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(team, index) in teams"
            :key="team.teamName"
            :class="{ 'row-changed': recentlyChanged[getTeamHighlightKey(team, index)] }"
          >
            <td>{{ team.isClassified ? index + 1 : '-' }}</td>
            <td>
              <div class="team-name">{{ team.teamName }}</div>
              <span v-if="!team.isClassified" class="badge bg-secondary">NKL</span>
            </td>
            <td>
              <div
                v-for="member in team.members"
                :key="member.id"
                class="member-line"
                :class="{ counted: member.counted }"
              >
                <span class="badge bg-secondary member-number">{{ member.number }}</span>
                <span class="member-name">{{ member.name }}</span>
                <span v-if="member.category_id === 30" class="badge bg-danger member-badge">K</span>
                <span class="member-time">{{ formatRunnerTime(member) }}</span>
              </div>
            </td>
            <td>
              <strong>{{ formatTeamTotal(team) }}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
  text-align: left;
}

.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  animation: highlight-row 1s linear;
}

@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

.team-name {
  font-weight: 600;
}

.member-line {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px;
  border-radius: 4px;
}

.member-line.counted {
  background: rgba(25, 135, 84, 0.14);
  font-weight: 600;
}

.member-number {
  min-width: 46px;
}

.member-time {
  margin-left: auto;
  font-family: monospace;
}

.member-badge {
  line-height: 1;
}

.status-bar {
  display: flex;
  gap: 8px;
}
</style>
